import axios from 'axios';

export type AiApiErrorCode =
  | 'unauthenticated' | 'forbidden' | 'validation' | 'not_found'
  | 'rate_limited' | 'server_error' | 'network_error' | 'aborted' | 'unknown';

export class AiApiError extends Error {
  constructor(
    public readonly code: AiApiErrorCode,
    message: string,
    public readonly status?: number,
    public readonly retryable = false,
  ) {
    super(message);
    this.name = 'AiApiError';
  }
}

const backendMessage = (error: unknown): string | undefined => {
  if (!axios.isAxiosError(error)) return undefined;
  const payload = error.response?.data as { error?: string | { message?: string } } | undefined;
  if (typeof payload?.error === 'string') return payload.error;
  return payload?.error?.message;
};

export function aiApiErrorForStatus(status: number, detail?: string): AiApiError {
  if (status === 400 || status === 422) return new AiApiError('validation', detail || 'The AI request is invalid.', status);
  if (status === 401) return new AiApiError('unauthenticated', 'Please sign in to continue.', status);
  if (status === 403) return new AiApiError('forbidden', 'You do not have permission to access this AI resource.', status);
  if (status === 404) return new AiApiError('not_found', 'The requested AI resource was not found.', status);
  if (status === 429) return new AiApiError('rate_limited', 'The AI service is busy. Please retry shortly.', status, true);
  if (status >= 500) return new AiApiError('server_error', 'The AI service is temporarily unavailable.', status, true);
  return new AiApiError('unknown', 'The AI request could not be completed.', status);
}

export function toAiApiError(error: unknown): AiApiError {
  if (error instanceof AiApiError) return error;
  if (error instanceof DOMException && error.name === 'AbortError') {
    return new AiApiError('aborted', 'The AI request was cancelled.');
  }
  if (axios.isAxiosError(error)) {
    const status = error.response?.status;
    if (status) return aiApiErrorForStatus(status, backendMessage(error));
    return new AiApiError('network_error', 'Unable to reach the AI service.', undefined, true);
  }
  return new AiApiError('unknown', 'The AI request could not be completed.');
}

export async function withAiApiError<T>(request: () => Promise<T>): Promise<T> {
  try { return await request(); } catch (error) { throw toAiApiError(error); }
}
